import React from "react";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { HOME } from "../../constants/testIds";
import { BOOKING_URL, IMAGES } from "../../lib/site";

// Signature on-load moment: masked line-by-line reveal, kinetic entrance, subtle 3D parallax hero.
// This is the largest craft moment on the site. Everything else supports this.

const EASE = [0.22, 1, 0.36, 1];

const containerStagger = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.06,
      delayChildren: 0.05,
    },
  },
};

const lineMask = {
  hidden: { y: "110%" },
  visible: {
    y: "0%",
    transition: { duration: 0.9, ease: EASE },
  },
};

const softRise = {
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 1.0, ease: EASE },
  },
};

const meta = {
  hidden: { opacity: 0, x: -12 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.9, ease: EASE },
  },
};

export default function KineticHero() {
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll();

  // Subtle 3D-feeling parallax on the hero image
  const imgY = useTransform(scrollYProgress, [0, 0.25], ["0%", "12%"]);
  const imgScale = useTransform(scrollYProgress, [0, 0.25], [1.02, 1.08]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 0.25], [1, 0.5]);
  const textY = useTransform(scrollYProgress, [0, 0.25], ["0%", "-6%"]);

  return (
    <section
      id="top"
      className="relative min-h-[100svh] overflow-hidden paper-grain"
    >
      {/* Background image with slow scale-in + parallax */}
      <motion.div
        className="absolute inset-0 will-change-transform"
        style={reduce ? undefined : { y: imgY, scale: imgScale }}
        initial={reduce ? false : { scale: 1.14, opacity: 0 }}
        animate={reduce ? undefined : { scale: 1.02, opacity: 1 }}
        transition={{ duration: 1.8, ease: EASE }}
      >
        <img
          src={IMAGES.hero}
          alt=""
          aria-hidden="true"
          fetchPriority="high"
          decoding="sync"
          className="absolute inset-0 w-full h-[115%] object-cover"
          style={{ filter: "saturate(0.85) contrast(0.96)" }}
        />
        <motion.div
          className="absolute inset-0 bg-gradient-to-b from-linen/80 via-linen/45 to-linen/70"
          style={reduce ? undefined : { opacity: overlayOpacity }}
        />
      </motion.div>

      {/* Large ghost number backdrop removed with the chapter concept */}

      {/* Content */}
      <motion.div
        className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-16 pt-20 md:pt-24 pb-24 md:pb-32 min-h-[100svh] flex flex-col justify-start"
        variants={containerStagger}
        initial="hidden"
        animate="visible"
        style={reduce ? undefined : { y: textY }}
      >
        {/* Meta row */}
        <div className="flex items-center gap-4 mb-6 md:mb-8">
          <motion.div
            variants={meta}
            className="h-[1px] w-10 md:w-14 bg-forest origin-left"
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 1.1, ease: EASE, delay: 0.1 }}
          />
          <motion.div
            variants={meta}
            className="font-meta text-forest"
          >
            Now open · Vaughan, ON
          </motion.div>
        </div>

        {/* Headline with masked line-by-line reveal */}
        <h1
          data-testid={HOME.heroHeadline}
          className="font-display text-[44px] sm:text-6xl md:text-7xl lg:text-[92px] leading-[1.02] tracking-tight text-umber max-w-5xl"
        >
          <span className="block overflow-hidden">
            <motion.span variants={lineMask} className="block">
              Where calm
            </motion.span>
          </span>
          <span className="block overflow-hidden">
            <motion.span variants={lineMask} className="block">
              meets{" "}
              <span className="font-display-italic text-forest">
                competence.
              </span>
            </motion.span>
          </span>
        </h1>

        {/* Supporting paragraph */}
        <motion.p
          variants={softRise}
          className="mt-8 max-w-xl text-base md:text-lg leading-relaxed text-umber/85"
        >
          A new dental home for Vaughan, where the quality of your care and the
          way you experience it matter equally.
        </motion.p>

        {/* CTAs */}
        <motion.div
          variants={softRise}
          className="mt-10 flex flex-col sm:flex-row gap-4"
        >
          <motion.a
            whileHover={reduce ? {} : { y: -2 }}
            whileTap={reduce ? {} : { y: 0 }}
            transition={{ duration: 0.3, ease: EASE }}
            href={BOOKING_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={HOME.heroPrimaryCta}
            className="group inline-flex items-center justify-center gap-3 bg-forest text-paper rounded-full px-9 py-4 text-[12px] tracking-[0.22em] uppercase hover:bg-sage transition-colors duration-300"
          >
            Book an Appointment
            <ArrowUpRight
              size={16}
              className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
            />
          </motion.a>
          <a
            href="#dr-mirza"
            data-testid={HOME.heroSecondaryCta}
            className="group inline-flex items-center justify-center gap-3 border border-umber/40 text-umber rounded-full px-9 py-4 text-[12px] tracking-[0.22em] uppercase hover:border-forest hover:text-forest transition-all duration-300"
          >
            Meet Dr. Mirza
          </a>
        </motion.div>
      </motion.div>

      {/* Scroll indicator */}
      <motion.div
        aria-hidden="true"
        className="hidden md:flex absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex-col items-center gap-3"
        initial={reduce ? false : { opacity: 0 }}
        animate={reduce ? undefined : { opacity: 1 }}
        transition={{ duration: 1, delay: 1.4, ease: EASE }}
      >
        <div className="font-meta text-umber/60">Scroll</div>
        <motion.div
          className="h-10 w-[1px] bg-umber/40 origin-top"
          animate={reduce ? undefined : { scaleY: [0, 1, 0] }}
          transition={{
            duration: 2.4,
            ease: "easeInOut",
            repeat: Infinity,
          }}
        />
      </motion.div>
    </section>
  );
}
